import { Input } from '@talentmesh/ui/components/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@talentmesh/ui/components/select';
import { useState } from 'react';
import useAtsJobs, { AtsJobResponse, AtsJobStatusFilter } from '../../api/useAtsJobs';
import AtsJobList from './AtsJobList';
import useDebounce from '@/Hooks/UseDebounce';
import UIStrings from '@/Utils/UIStrings';

/** Long enough that typing a job title is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 350;

const statusFilterLabels: Record<AtsJobStatusFilter, string> = {
    All: UIStrings.AtsJobStatusFilterAll,
    Open: UIStrings.AtsJobStatusOpen,
    Closed: UIStrings.AtsJobStatusClosed,
    Draft: UIStrings.AtsJobStatusDraft,
    Archived: UIStrings.AtsJobStatusArchived,
};

// Unknown is deliberately absent: it is what a job's status degrades to when we cannot read it, not
// something the ATS can be asked for.
const statusFilterOptions: AtsJobStatusFilter[] = ['All', 'Open', 'Closed', 'Draft', 'Archived'];

interface AtsJobPickerProps {
    companyId: string;
    selectedJob: AtsJobResponse | null;
    onSelectedJobChange: (job: AtsJobResponse) => void;
}

/**
 * The job source a recruiter picks to import from.
 *
 * Owns the query — search, status filter and paging — because all three are one question asked of the
 * ATS. Rendering the resulting list and its "load more" control is delegated to {@link AtsJobList}.
 */
const AtsJobPicker = ({ companyId, selectedJob, onSelectedJobChange }: AtsJobPickerProps) => {
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<AtsJobStatusFilter>('Open');

    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

    const { data, size, setSize, isValidating, error } = useAtsJobs({
        companyId,
        status,
        nameContains: debouncedSearch,
        enabled: true,
    });

    // Pages accumulate: every page loaded so far, flattened into one list. Changing the filter resets
    // this back to the first page (the hook drops the accumulation when its first key changes).
    const jobs = data ? data.flatMap((page) => page.jobs) : [];
    const hasFilters = debouncedSearch.trim().length > 0 || status !== 'All';

    const lastPage = data?.[data.length - 1];
    const hasMore = !!lastPage?.nextCursor;

    // No pages yet is the initial load; a fetch in flight once pages exist is a "load more".
    const isLoadingInitial = !data && !error;
    const isLoadingMore = !!data && isValidating;

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4" data-cy="AtsJobPicker">
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                <Input
                    className="min-w-0 flex-1"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={UIStrings.SearchAtsJobsPlaceholder}
                    aria-label={UIStrings.SearchAtsJobsPlaceholder}
                    autoComplete="off"
                    data-cy="AtsJobSearch"
                />
                <Select value={status} onValueChange={(value) => setStatus(value as AtsJobStatusFilter)}>
                    <SelectTrigger
                        className="sm:w-44"
                        aria-label={UIStrings.AtsJobStatusFilterAll}
                        data-cy="AtsJobStatusFilter"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {statusFilterOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                                {statusFilterLabels[option]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <AtsJobList
                jobs={jobs}
                selectedJob={selectedJob}
                onSelectedJobChange={onSelectedJobChange}
                isLoading={isLoadingInitial}
                isError={!!error}
                hasFilters={hasFilters}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={() => setSize(size + 1)}
            />
        </div>
    );
};

export default AtsJobPicker;
